import type { SlugConflict } from '@/lib/prePublishGate';

export type SlugAvailabilityStatus =
  | 'idle'
  | 'checking'
  | 'available'
  | 'conflict'
  | 'invalid'
  | 'error';

export type SlugAvailability = {
  /** The candidate this result belongs to. It prevents a prior result from authorizing/warning on a newer edit. */
  slug?: string;
  status: SlugAvailabilityStatus;
  /** Reuses the #537 slug-collision conflict shape (url/slug/kind/title/source) — same backend model, one vocabulary. */
  conflict?: SlugConflict | null;
};

/**
 * Slug availability is a SOFT, advisory guardrail — never a hard block.
 *
 * A `conflict`/`invalid`/`error` result is surfaced inline (see
 * PublishingSettingsCard) so the user can make an informed call, but the
 * metadata autosave PATCH still goes through: the publish-time
 * `slug_collision` gate (PR #537) already offers an overridable "Publish
 * anyway" warning, and WordPress itself dedupes conflicting slugs on write
 * (`my-slug` -> `my-slug-2`). Blocking autosave on top of that would just
 * strand the user's other field edits behind a slug they may fix later.
 *
 * The one case still worth holding: autosaving while a check is actively
 * in flight for this EXACT candidate. That isn't a data-safety concern — it
 * only avoids racing the availability GET against the metadata PATCH — so
 * saving proceeds again as soon as that check resolves (to any status).
 */
export function canAutosaveSlugChange(
  nextSlug: string,
  persistedSlug: string,
  availabilityStatus: SlugAvailabilityStatus,
  availabilitySlug?: string,
): boolean {
  const candidate = nextSlug.trim();
  if (nextSlug === persistedSlug || !candidate) return true;

  return !(availabilityStatus === 'checking' && availabilitySlug === candidate);
}
